import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService, TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { TreeSelectModule } from 'primeng/treeselect';
import { firstValueFrom, forkJoin } from 'rxjs';
import { SETTINGS, Settings } from '../../../app.settings';
import { isNotEmpty } from '../../../common/functions';
import { warningMsg } from '../../../common/messages';
import { notifyAndTrackError } from '../../../common/error';
import { ProcessingComponent } from '../../../components/processing/processing.component';
import { AppInsightsService } from '../../../services/app-insights.service';
import { ILocation, IOrderAddRequest } from '../../../services/insecta';
import { OrderService } from '../../../services/insecta/order.service';
import { MSGraphService } from '../../../services/msgraph.service';

@Component({
  selector: 'app-new-order-dialog',
  standalone: true,
  imports: [ButtonModule, CommonModule, DialogModule, TreeSelectModule, FloatLabelModule, SelectModule, FormsModule, ProcessingComponent],
  templateUrl: './new-order-dialog.component.html'
})
export class NewOrderDialogComponent implements OnInit {
  dialogVars: any = {
    currentUser: ``,
    site: null,
    location: null,
    visible: false,
    parent: [],
    title: `New Order`,
    isReady: true,
    pageId: `Orders:NewOrderDialog`
  };

  sites: any[] = [];
  locationNodes: TreeNode[] = [];
  locations: ILocation[] = [];
  locationEnabled = false;
  visible: boolean = false;

  constructor(
    private appInsights: AppInsightsService,
    private orderService: OrderService,
    private graphService: MSGraphService,
    private router: Router,
    private msgService: MessageService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.dialogVars.data = <ILocation>{};
  }

  ngOnInit(): void {
    const request = {
      locations: this.orderService.getLocationsForHierarchy(),
      profile: this.graphService.getMyProfile$()
    };
    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.locations = response.locations;
        this.sites = this.locations.map((location) => ({
          label: location.name,
          value: location.id
        }));
        this.dialogVars.profile = response.profile;
        this.dialogVars.currentUser = response.profile.mail;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'NewOrderDialogComponent', action: 'InitLoad' });
      });
  }

  openNewOrderDialog() {
    this.visible = true;
  }

  onSiteSelect() {
    this.locationEnabled = true;
    const deliveryLocation = this.locations.find((loc) => loc.id === this.dialogVars.site);
    if (deliveryLocation) {
      this.locationNodes = deliveryLocation?.children?.length
        ? deliveryLocation.children.map((child: ILocation) => this.buildNode(child))
        : [this.buildNode(deliveryLocation)];
    }
    this.dialogVars.location = null;
  }

  buildNode(location: ILocation): TreeNode {
    location.locationTypeId = location.locationTypeId.toUpperCase();
    location.containmentTypeId = location.containmentTypeId?.toUpperCase();
    if (isNotEmpty(location.address)) {
      location.address!.privateUserId = location.address!.privateUserId?.toUpperCase();
    }
    const childrenNodes = (location?.children || []).map((x) => this.buildNode(x));

    return {
      key: location.id,
      label: location.name,
      data: location,
      selectable: location.isSelectable,
      children: childrenNodes
    };
  }

  createNewOrder() {
    if (!this.dialogVars.site) {
      warningMsg(this.msgService, 'Please select a site');
      return;
    }
    if (!this.dialogVars.location) {
      warningMsg(this.msgService, 'Please select a delivery location');
      return;
    }
    this.dialogVars.isReady = false;
    this.visible = false;
    const currentYear = new Date().getFullYear();
    const requesterEmail = this.dialogVars.profile.userPrincipalName;
    const alternateOwnerData = [{ name: this.dialogVars.profile.displayName, email: requesterEmail }];
    const newOrder: IOrderAddRequest = {
      catalogId: this.dialogVars.site,
      deliveryLocationId: this.dialogVars.location?.data?.id,
      orderYear: currentYear.toString().slice(-2),
      notificationJson: '',
      alternateOwnerJson: JSON.stringify(alternateOwnerData),
      orderName: '',
      requester: requesterEmail
    };

    firstValueFrom(this.orderService.addOrder(newOrder))
      .then((res) => {
        this.dialogVars.isReady = true;
        this.router.navigateByUrl(`orders/${res}/requester`);
      })
      .catch((err) => {
        this.dialogVars.isReady = true;
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'NewOrderDialogComponent', action: 'CreateNewOrder' });
      });
  }

  cancelNewOrder() {
    this.resetDialogState();
    this.visible = false;
  }

  private resetDialogState(): void {
    this.dialogVars.site = null;
    this.dialogVars.location = null;
    this.locationEnabled = false;
    this.locationNodes = [];
  }
}
